/** @format */

import { map, Observable, of } from 'rxjs';
import { computed, inject, Injectable } from '@angular/core';
import {
  BeaconRequestBody,
  BeaconQueryParameters,
  Brand,
  Chain,
  CriteoDigitalShelfTalker,
  CriteoPlacement,
  CriteoProduct,
  CriteoTrackTransactionMetadata,
  CriteoTrackTransactionResponse,
  CriteoVideo,
  CriteoViewItemMetadata,
  CriteoViewItemResponse,
  CriteoViewProductsResponse,
  OcpRequestBody,
  PdpRequestBody,
  PlpInGridSponsoredProduct,
  PlpProductInformation,
  PlpRequestBody,
  ProductInformation,
  ProductTypes,
  SrlpProductInformation,
  SrlpRequestBody,
  TransactionInformation,
  UserInformation,
} from '@shared/data-access/interfaces';
import {
  CRITEO_CAROUSEL_MAXIMUM_PRODUCTS,
  EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE,
  EMPTY_CRITEO_VIEW_ITEM_RESPONSE,
  EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE,
} from './criteo.constants';
import { KillswitchService, WINDOW } from '@shared/data-access/services';
import { DmnAdServerDao } from './dmn-ad-server.dao';
import { CriteoService } from './criteo.service';

@Injectable({
  providedIn: 'root',
})
export class DmnAdService {
  private readonly criteoService = inject(CriteoService);
  private readonly killswitchService = inject(KillswitchService);
  private readonly window = inject(WINDOW);
  private readonly chain = inject(Chain);
  private readonly dmnAdServerDao = inject(DmnAdServerDao);

  private readonly plpDmnAdServerEnabled =
    this.killswitchService.killSwitchForCurrentChain('plpDmnAdServerEnabled');

  private readonly srlpDmnAdServerEnabled =
    this.killswitchService.killSwitchForCurrentChain('srlpDmnAdServerEnabled');

  private readonly pdpDmnAdServerEnabled =
    this.killswitchService.killSwitchForCurrentChain('pdpDmnAdServerEnabled');

  private readonly ocpDmnAdServerEnabled =
    this.killswitchService.killSwitchForCurrentChain('ocpDmnAdServerEnabled');

  private readonly plpOrSrlpKillswitch = computed(() => {
    const pageType = this.window._dsgTag?.pageType;
    if (pageType?.Name === 'Family') {
      return this.plpDmnAdServerEnabled();
    } else if (pageType?.Name === 'Search') {
      return this.srlpDmnAdServerEnabled();
    } else {
      return false;
    }
  });

  private readonly routeBeaconToAdServer = computed(
    () =>
      this.pdpDmnAdServerEnabled() || this.ocpDmnAdServerEnabled() || this.plpOrSrlpKillswitch(),
  );

  private lastInGridOnLoadBeacon = ''; // This is used for the inGrid onLoadBeacons. This is to prevent two criteo calls to be made
  private lastDSTOnLoadBeacon = '';

  getPdpAds(viewItemMetadata: CriteoViewItemMetadata): Observable<CriteoViewItemResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_VIEW_ITEM_RESPONSE });
    }

    const dsgTag = this.window._dsgTag;
    const userInfo: UserInformation = {
      adobeMCVID: dsgTag?.CustomerIDs?.AdobeMCVID || '',
      optedOut: false,
    };
    this.addRequestFieldIfPresent(userInfo, 'email', dsgTag?.CustomerIDs?.HashedEmail);
    this.addRequestFieldIfPresent(
      userInfo,
      'athleteId',
      this.validateNumberFromString(dsgTag?.CustomerIDs?.AthleteID, false),
    );
    this.addRequestFieldIfPresent(userInfo, 'swimlane', dsgTag?.customerLane?.toString());

    const productInfo: ProductInformation = {
      parentItem: viewItemMetadata.parentItem || '',
      item: this.validateNumberFromString(viewItemMetadata.item, true),
      price: this.validateNumberFromString(viewItemMetadata.price, true),
      availability: viewItemMetadata.availability,
    };
    this.addRequestFieldIfPresent(productInfo, 'category', viewItemMetadata.category);

    const requestBody: PdpRequestBody = {
      brand: this.chain.chainIdentifierAbbr.toUpperCase() as Brand,
      platform: dsgTag?.experience === 'Mobile' ? 'MWEB' : 'DESKTOP',
      userInformation: userInfo,
      productInformation: productInfo,
    };

    return this.dmnAdServerDao.getPdpAds(requestBody);
  }

  getPlpAds(metadata: PlpProductInformation): Observable<CriteoViewProductsResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
    }

    if (this.plpDmnAdServerEnabled()) {
      const dsgTag = this.window._dsgTag;
      const userInfo: UserInformation = {
        adobeMCVID: dsgTag?.CustomerIDs?.AdobeMCVID || '',
        optedOut: false,
      };
      this.addRequestFieldIfPresent(userInfo, 'email', dsgTag?.CustomerIDs?.HashedEmail);
      this.addRequestFieldIfPresent(
        userInfo,
        'athleteId',
        this.validateNumberFromString(dsgTag?.CustomerIDs?.AthleteID, false),
      );
      this.addRequestFieldIfPresent(userInfo, 'swimlane', dsgTag?.customerLane?.toString());

      const plpProductInfo: PlpProductInformation = {
        ...metadata,
        pageNumber: metadata.pageNumber === null ? undefined : metadata.pageNumber + 1, // DMN Ad Server uses 1-based indexing for pages
      };

      const requestBody: PlpRequestBody = {
        brand: this.chain.chainIdentifierAbbr.toUpperCase() as Brand,
        platform: dsgTag?.experience === 'Mobile' ? 'MWEB' : 'DESKTOP',
        userInformation: userInfo,
        plpProductInformation: plpProductInfo,
      };

      return this.dmnAdServerDao.getPlpAds(requestBody).pipe(
        map((response: CriteoViewProductsResponse) => {
          return this.formatCriteoFamilyAds(response);
        }),
      );
    } else {
      const criteoFilters =
        metadata.filters && Object.keys(metadata.filters).length > 0
          ? this.criteoService.parseFilters(metadata.filters)
          : null;
      return this.criteoService.viewProductsPage({
        productType: ProductTypes.VIEW_CATEGORY,
        category: metadata.category,
        listSize: metadata.listSize,
        pageNumber: metadata.pageNumber,
        filters: criteoFilters,
      });
    }
  }

  getSrlpAds(metadata: SrlpProductInformation): Observable<CriteoViewProductsResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE });
    }

    if (this.srlpDmnAdServerEnabled()) {
      const dsgTag = this.window._dsgTag;
      const userInfo: UserInformation = {
        adobeMCVID: dsgTag?.CustomerIDs?.AdobeMCVID || '',
        optedOut: false,
      };
      this.addRequestFieldIfPresent(userInfo, 'email', dsgTag?.CustomerIDs?.HashedEmail);
      this.addRequestFieldIfPresent(
        userInfo,
        'athleteId',
        this.validateNumberFromString(dsgTag?.CustomerIDs?.AthleteID, false),
      );
      this.addRequestFieldIfPresent(userInfo, 'swimlane', dsgTag?.customerLane?.toString());

      const srlpProductInfo: SrlpProductInformation = {
        ...metadata,
        pageNumber: metadata.pageNumber === null ? undefined : metadata.pageNumber + 1, // DMN Ad Server uses 1-based indexing for pages
      };

      const requestBody: SrlpRequestBody = {
        brand: this.chain.chainIdentifierAbbr.toUpperCase() as Brand,
        platform: dsgTag?.experience === 'Mobile' ? 'MWEB' : 'DESKTOP',
        userInformation: userInfo,
        srlpProductInformation: srlpProductInfo,
      };

      return this.dmnAdServerDao.getSrlpAds(requestBody).pipe(
        map((response: CriteoViewProductsResponse) => {
          return this.formatCriteoFamilyAds(response);
        }),
      );
    } else {
      const criteoFilters =
        metadata.filters && Object.keys(metadata.filters).length > 0
          ? this.criteoService.parseFilters(metadata.filters)
          : null;
      return this.criteoService.viewProductsPage({
        productType: ProductTypes.VIEW_SEARCH_RESULT,
        category: metadata.searchTerm,
        listSize: metadata.listSize,
        pageNumber: metadata.pageNumber,
        filters: criteoFilters,
      });
    }
  }

  getOcpAds(
    trackTransactionMetadata: CriteoTrackTransactionMetadata,
  ): Observable<CriteoTrackTransactionResponse> {
    if (this.isOptedOut()) {
      return of({ ...EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE });
    }

    const dsgTag = this.window._dsgTag;
    const userInfo: UserInformation = {
      adobeMCVID: dsgTag?.CustomerIDs?.AdobeMCVID || '',
      optedOut: false,
    };

    this.addRequestFieldIfPresent(userInfo, 'email', dsgTag?.CustomerIDs?.HashedEmail);
    this.addRequestFieldIfPresent(
      userInfo,
      'athleteId',
      this.validateNumberFromString(dsgTag?.CustomerIDs?.AthleteID, false),
    );
    this.addRequestFieldIfPresent(userInfo, 'swimlane', dsgTag?.customerLane?.toString());

    const transactionInfo: TransactionInformation = {
      items: trackTransactionMetadata.items.map((item) =>
        this.validateNumberFromString(item, true),
      ),
      prices: trackTransactionMetadata.prices.map((price) =>
        this.validateNumberFromString(price, true),
      ),
      quantities: trackTransactionMetadata.quantities,
      transactionId: trackTransactionMetadata.transactionId,
    };
    this.addRequestFieldIfPresent(transactionInfo, 'currency', trackTransactionMetadata.currency);

    const requestBody: OcpRequestBody = {
      brand: this.chain.chainIdentifierAbbr.toUpperCase() as Brand,
      platform: dsgTag?.experience === 'Mobile' ? 'MWEB' : 'DESKTOP',
      userInformation: userInfo,
      transactionInformation: transactionInfo,
    };

    return this.dmnAdServerDao.getOcpAds(requestBody);
  }

  /**
   * Builds the pipe-delimited list of ProductIds for products beyond
   * `CRITEO_CAROUSEL_MAXIMUM_PRODUCTS` so they can be reported as excluded on
   * the onLoad beacon. Returns `undefined` when there are no excluded products.
   */
  buildExcludedCriteoProductsParam(products: CriteoProduct[] | undefined): string | undefined {
    const excluded = products?.slice(CRITEO_CAROUSEL_MAXIMUM_PRODUCTS) ?? [];
    return excluded.length ? excluded.map((p) => p.ProductId).join('|') : undefined;
  }

  // Turn beaconQueryParameters into an interface and use that instead of calling the adService twice.
  sendBeacon(
    beacon: string,
    beaconQueryParameters: BeaconQueryParameters,
    excluded?: string,
  ): void {
    if (!beacon) return;

    if (this.routeBeaconToAdServer()) {
      const queryParamsString = this.buildSendBeaconQueryParameters(beaconQueryParameters);
      const finalUrl = excluded ? `${beacon}&ex=${excluded}` : beacon;
      const requestBody: BeaconRequestBody = {
        beacon: finalUrl,
        referer: this.window.location?.href ?? '',
      };
      this.dmnAdServerDao.sendBeacon(requestBody, queryParamsString).subscribe();
    } else {
      this.criteoService.fireBeacon(beacon, excluded);
    }
  }

  checkOnLoadBeaconFiring(
    sponsoredSEO: string,
    beaconURL: string,
    sponsoredProductCopy: PlpInGridSponsoredProduct[] | null,
    skippedIDs: string[],
  ): void {
    if (sponsoredSEO === 'N') {
      return;
    }

    if (sponsoredProductCopy?.length) {
      for (const skipped of sponsoredProductCopy) {
        skippedIDs.push(skipped.product.ID);
      }
    }

    const excludedProductsString = skippedIDs.join('|');
    this.fireInGridOnLoadBeacon(beaconURL, excludedProductsString);
  }

  fireInGridOnLoadBeacon(beaconURL: string, excludedProducts = ''): void {
    if (!beaconURL || beaconURL === this.lastInGridOnLoadBeacon) {
      return;
    }
    const beaconQueryParameters: BeaconQueryParameters = {
      placementType: 'INGRID',
      placementLevel: 'PLACEMENT',
      beaconType: 'LOAD',
    };

    this.sendBeacon(beaconURL, beaconQueryParameters, excludedProducts);
    this.lastInGridOnLoadBeacon = beaconURL;
  }

  fireDSTOnLoadBeacon(beaconURL: string): void {
    if (!beaconURL || beaconURL === this.lastDSTOnLoadBeacon) {
      return;
    }
    const beaconQueryParameters: BeaconQueryParameters = {
      placementType: 'DST',
      placementLevel: 'PLACEMENT',
      beaconType: 'LOAD',
    };

    this.sendBeacon(beaconURL, beaconQueryParameters);
    this.lastDSTOnLoadBeacon = beaconURL;
  }

  private buildSendBeaconQueryParameters(queryParams: BeaconQueryParameters): string {
    const pageTypeParam = this.getPageTypeParam();
    return `${pageTypeParam}placementType=${queryParams.placementType}&placementLevel=${queryParams.placementLevel}&beaconType=${queryParams.beaconType}`;
  }

  private getPageTypeParam(): string {
    const pageTypeString = 'pageType=';
    switch (this.window._dsgTag?.pageType?.Name) {
      case 'Family':
        return `${pageTypeString}PLP&`;
      case 'Search':
        return `${pageTypeString}SRLP&`;
      case 'Product Detail':
        return `${pageTypeString}PDP&`;
      case 'Order Confirmation':
        return `${pageTypeString}OCP&`;
      default:
        return '';
    }
  }

  private formatCriteoFamilyAds(response: CriteoViewProductsResponse): CriteoViewProductsResponse {
    const carousel: CriteoPlacement[] = [];
    const inGrid: CriteoPlacement[] = [];
    let digitalShelfTalker: CriteoDigitalShelfTalker | undefined;
    const videos: CriteoVideo[] = [];
    this.populateObjectPlacements(
      response,
      carousel,
      inGrid,
      (dst) => {
        digitalShelfTalker = dst;
      },
      videos,
    );
    this.ensureNonEmptyPlacements(carousel, inGrid, videos);
    const res: CriteoViewProductsResponse = {
      // OnLoadBeacon is only returned by criteo qa and not used, but is required by the interface
      OnLoadBeacon: '',
      placements: {
        carousel,
        inGrid,
        ...(digitalShelfTalker ? { digitalShelfTalker } : {}),
        videos,
      },
    };
    return res;
  }

  /**
   * Conditionally adds an optional field to a request body object in place.
   * If the value is null or undefined, the field will not be added to the object.
   *
   * @param obj The request body object to mutate.
   * @param key The key of the field to be added.
   * @param value The value to assign. If null or undefined or blank, the field is skipped.
   */
  private addRequestFieldIfPresent<T extends object, K extends keyof T>(
    obj: T,
    key: K,
    value: T[K] | null | undefined,
  ): void {
    if (value !== null && value !== undefined && value !== '') {
      obj[key] = value;
    }
  }

  /**
   * Parses a string to a finite number, returning a fallback based on whether the field is required.
   * Guards against empty strings, which `Number()` would otherwise coerce to 0.
   *
   * @param value The string to parse.
   * @param required Whether the field is required as a part of the request body.
   * @returns The parsed finite number if valid, 0 if `required` is true and the value is invalid, or null otherwise.
   */
  private validateNumberFromString(value: string, required: boolean): number | null {
    if (value) {
      const num = Number(value);
      if (Number.isFinite(num)) {
        return num;
      }
    }
    return required ? 0 : null;
  }

  private isOptedOut(): boolean {
    if (this.window._dsgTag?.DoNotSell) {
      return true;
    }
    return false;
  }

  private populateObjectPlacements(
    response: CriteoViewProductsResponse,
    carousel: CriteoPlacement[],
    inGrid: CriteoPlacement[],
    setDigitalShelfTalker: (dst: CriteoDigitalShelfTalker | undefined) => void,
    videos: CriteoVideo[],
  ): void {
    carousel.push(...(response.placements.carousel ?? []));
    inGrid.push(...(response.placements.inGrid ?? []));
    setDigitalShelfTalker(response.placements.digitalShelfTalker);
    videos.push(...(response.placements.videos ?? []));
  }

  private ensureNonEmptyPlacements(
    carousel: CriteoPlacement[],
    inGrid: CriteoPlacement[],
    videos: CriteoVideo[],
  ): void {
    if (carousel.length === 0) {
      carousel.push({ products: [] } as CriteoPlacement);
    }
    if (inGrid.length === 0) {
      inGrid.push({ products: [] } as CriteoPlacement);
    }
    if (videos.length === 0) {
      videos.push(this.buildEmptyVideoPlacement());
    }
  }

  private buildEmptyVideoPlacement(): CriteoVideo {
    return {
      format: '',
      LineItemID: '',
      OnBundleBasketChangeBeacon: '',
      OnClickBeacon: '',
      OnFileClickBeacon: '',
      OnLoadBeacon: '',
      OnViewBeacon: '',
      TagVideoVAST: '',
      products: [],
      products_order: [],
      rendering: {
        background_video_alt_text: '',
        color_mode: '',
        optional_redirect_target: '',
        optional_legal_text: '',
      },
    };
  }
}
